import '@fs/react-scripts/polyfills'
import React, { useState, Suspense } from 'react'
import ReactDOM from 'react-dom'
import { ApolloClient, InMemoryCache, HttpLink } from '@apollo/client'
import { ApolloProvider } from '@apollo/client/react'
import { appPath, mergeSentryConfig, sentryDSN } from '@fs/zion-config'
import { essentialLocales, setAppLocales, i18n } from '@fs/zion-locale'
import { useExperiment } from '@fs/zion-experiments'
import { useFeatureFlag } from '@fs/zion-flags'
import Root, { FrontierBaseProviders } from '@fs/zion-root'
import { Router } from '@fs/zion-router'
import { AppLayout } from '@fs/zion-ui'
import * as Sentry from '@sentry/browser'

import { NoticeLoading } from '@fs/zion-icon'
import { CommonUseProvider } from 'contexts'
import { getSessionId } from '@fs/zion-session'
import { LocalDataCacheProvider } from 'hooks'
import App from './App'
import './locales'
import * as serviceWorker from './serviceWorker'

// Enable local feature flag development using the file "dev.flags.js"
// If that file exports anything truthy it will initialize feature flags in localhost mode
// and use the flags defined in that file, instead of flags defined in split.
const mockFlags = process.env.NODE_ENV === 'development' ? import('./dev.flags').then((m) => m.default) : undefined
const mockActivities =
  process.env.NODE_ENV === 'development' ? import('./dev.activities').then((m) => m.default) : undefined

// initialize Sentry for the app
if (sentryDSN) {
  // pass Sentry overrides to mergeSentryConfig
  Sentry.init(
    mergeSentryConfig({
      dsn: sentryDSN,
      ignoreErrors: ['-extension:'], // Ignore browser extension errors
    })
  )
}

setAppLocales(essentialLocales)

// Here we pass the mounted app path from the server to the client.
const base = appPath ? new URL(appPath).pathname : ''

function FrontierRoot() {
  const [rmsEndpointAuthEx] = useExperiment('rmsEndpointAuthEx')
  // While camera operators move from Phoenix to COP, lightyear-data reads this header per request to
  // pick the source. Set once here so every camera operator query follows the flag.
  const newCOFEndpoints = useFeatureFlag('lightyear_lightyearReact_useCopOperatorListEndpoint')?.isOn
  // Provide a way to toggle the footer
  const [disableFooter, setDisableFooter] = useState(() => false)
  const [disableHeader, setDisableHeader] = useState(false)
  const [disableSubheader, setDisableSubheader] = useState(false)
  // Provide a way to set the background color for the full page
  const [backgroundColor, setBackgroundColor] = useState(() => '')
  // Set to true to prevent vertical scrolling on the page (Per Matthew Larson, when true this helps keep the page full height)
  const [noScrollLayout, setNoScrollLayout] = useState(false)
  // Provide a way to use fullScreen mode of zion-root
  const [fullScreen, setFullScreen] = useState(() => false)

  // setup Apollo Client
  const httpLink = new HttpLink({
    uri: '/records/images/orchestration/',
    headers: {
      Authorization: `Bearer ${getSessionId()}`,
      'Timezone-Offset': new Date().getTimezoneOffset(),
      'User-Locale': i18n.language,
      // Apollo Client 4 builds the request from the options given to the link, so this header is set
      // here instead of being patched onto `client.link.options` after the client is created.
      ...(rmsEndpointAuthEx ? { rmsendpointauthex: 'true' } : {}),
      'use-new-cop-endpoint': newCOFEndpoints ? 'true' : 'false',
    },
  })

  const client = new ApolloClient({
    cache: new InMemoryCache({
      // Custom cache behavior for specific GraphQL types:
      // - Address: Uses composite key (email + fullName) instead of id for cache normalization
      // - CameraProject.cameraOperators: Replaces cached data instead of merging to prevent duplicates
      typePolicies: {
        Address: {
          keyFields: (address, context) => {
            const fields = []
            if (context.readField('email')) fields.push('email')
            if (context.readField('fullName')) fields.push('fullName')
            return fields
          },
        },
        CameraProject: {
          fields: {
            cameraOperators: {
              merge(_existing, incoming) {
                return incoming
              },
            },
          },
        },
      },
    }),
    link: httpLink,
  })

  const rootProps = {
    analytics: true,
    footer: { hidden: disableFooter },
    fullScreen,
    header: { hidden: disableHeader },
    helpChatbot: false,
  }

  const appLayoutProps = {
    backgroundColor,
  }

  const defaultValues = {
    disableSubheader,
    setBackgroundColor,
    setDisableFooter,
    setDisableHeader,
    setDisableSubheader,
    setFullScreen,
    setNoScrollLayout,
  }

  return client ? (
    <React.StrictMode>
      <Suspense fallback={<NoticeLoading />}>
        <ApolloProvider client={client}>
          <Router basename={base}>
            <Root {...rootProps}>
              <AppLayout fullWidth noScroll={noScrollLayout} {...appLayoutProps}>
                <CommonUseProvider defaultValues={defaultValues}>
                  <LocalDataCacheProvider>
                    <App />
                  </LocalDataCacheProvider>
                </CommonUseProvider>
              </AppLayout>
            </Root>
          </Router>
        </ApolloProvider>
      </Suspense>
    </React.StrictMode>
  ) : null
}

ReactDOM.render(
  <FrontierBaseProviders mockFlags={mockFlags} mockActivities={mockActivities}>
    <FrontierRoot />
  </FrontierBaseProviders>,
  document.getElementById('root')
)

// If you want your app to work offline and load faster, you can change
// unregister() to register() below. Note this comes with some pitfalls.
// Learn more about service workers: https://bit.ly/CRA-PWA
serviceWorker.unregister()
